import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import StarField from './StarField';
import AdUnit from './AdUnit';

// ─── Testimonios ──────────────────────────────────────────
const TESTIMONIOS = [
  {
    inicial: 'C',
    nombre:  'Carmen V.',
    lugar:   'México',
    texto:   '"Lo que dijo sobre mi relación fue tan preciso que lloré. Nadie más lo sabía."',
    color:   'rgba(255,215,0,0.07)',
    borde:   'rgba(255,215,0,0.18)',
  },
  {
    inicial: 'D',
    nombre:  'Diego M.',
    lugar:   'Argentina',
    texto:   '"Soy escéptico total. Pero estuvo tan cerca de la realidad que me dejó pensando días."',
    color:   'rgba(147,112,219,0.07)',
    borde:   'rgba(147,112,219,0.2)',
  },
  {
    inicial: 'V',
    nombre:  'Valentina R.',
    lugar:   'Colombia',
    texto:   '"Lo mandé al grupo de WhatsApp y todas quisieron hacerlo. Increíble."',
    color:   'rgba(233,69,96,0.07)',
    borde:   'rgba(233,69,96,0.18)',
  },
];

// ─── Contador animado ─────────────────────────────────────
function CounterAnimado({ target = 13847 }) {
  const [val, setVal] = useState(target - 800);
  useEffect(() => {
    let current = target - 800;
    const step  = Math.ceil(800 / 55);
    const timer = setInterval(() => {
      current += step;
      if (current >= target) { setVal(target); clearInterval(timer); return; }
      setVal(current);
    }, 28);
    return () => clearInterval(timer);
  }, [target]);
  return <span>{val.toLocaleString('es')}</span>;
}

// ─── Estrellas de rating ──────────────────────────────────
function Stars() {
  return (
    <span style={{ color: '#FFD700', letterSpacing: '2px', fontSize: '0.85rem' }}>
      ★★★★★
    </span>
  );
}

// ─── Landing ──────────────────────────────────────────────
function Landing() {
  const navigate = useNavigate();

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>

      {/* Fondo: estrellas + nebulas */}
      <StarField count={200} />
      <div className="nebula-blob nebula-1" />
      <div className="nebula-blob nebula-2" />
      <div className="nebula-blob nebula-3" />

      {/* Contenido principal */}
      <div style={{
        position:       'relative',
        zIndex:         1,
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '100vh',
        textAlign:      'center',
        padding:        '3rem 1.5rem 4rem',
        gap:            0,
      }}>

        {/* ── MARCA ──────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <div style={{
            fontFamily:  'var(--fuente-titulo)',
            fontSize:    'clamp(2.8rem, 9vw, 5.2rem)',
            fontWeight:  '700',
            letterSpacing: '0.18em',
            color:       'var(--color-oro)',
            textShadow:  '0 0 40px rgba(255,215,0,0.5), 0 0 90px rgba(255,215,0,0.18)',
            lineHeight:  1,
            marginBottom: '0.4rem',
          }}>
            SELENE
          </div>
          <div style={{
            fontFamily:    'var(--fuente-titulo)',
            fontSize:      'clamp(0.58rem, 2vw, 0.78rem)',
            letterSpacing: '0.4em',
            color:         'rgba(255,215,0,0.45)',
            textTransform: 'uppercase',
          }}>
            Quiromancia Ancestral
          </div>
        </motion.div>

        {/* ── DIVIDER ORO ────────────────────────────────── */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.9 }}
          style={{
            width:      '180px',
            height:     '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.6), transparent)',
            margin:     '1.6rem auto',
          }}
        />

        {/* ── PALMA FLOTANTE ─────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.4 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.9, type: 'spring', stiffness: 70 }}
          style={{ position: 'relative', marginBottom: '2rem' }}
        >
          {/* Anillo externo pulsante */}
          <div style={{
            position:     'absolute',
            inset:        '-22px',
            borderRadius: '50%',
            background:   'radial-gradient(circle, rgba(255,215,0,0.10) 0%, transparent 70%)',
            animation:    'glow-pulse 3.5s ease-in-out infinite',
          }} />
          {/* Anillo giratorio */}
          <div style={{
            position:     'absolute',
            inset:        '-8px',
            borderRadius: '50%',
            border:       '1px solid rgba(255,215,0,0.18)',
            animation:    'rotate-slow 14s linear infinite',
          }} />
          <div style={{
            fontSize:  'clamp(5rem, 14vw, 7.5rem)',
            lineHeight: 1,
            filter:    'drop-shadow(0 0 28px rgba(255,215,0,0.4)) drop-shadow(0 0 60px rgba(255,215,0,0.12))',
            animation: 'float-hand 4.5s ease-in-out infinite',
          }}>
            ✋
          </div>
        </motion.div>

        {/* ── HEADLINE ───────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          style={{ maxWidth: '520px', marginBottom: '1.8rem' }}
        >
          <h1 style={{
            fontFamily:  'var(--fuente-titulo)',
            fontSize:    'clamp(1.25rem, 4vw, 1.9rem)',
            fontWeight:  '400',
            color:       'var(--color-texto)',
            lineHeight:  1.5,
            letterSpacing: '0.02em',
            marginBottom: '0.8rem',
          }}>
            Secretos que tu mano lleva grabados…<br />
            <span style={{ color: 'var(--color-oro)' }}>y aún no has escuchado</span>
          </h1>
          <p style={{
            color:      'var(--color-texto-suave)',
            fontSize:   'clamp(0.85rem, 2.5vw, 0.98rem)',
            fontStyle:  'italic',
            lineHeight: 1.75,
          }}>
            Selene analiza las líneas de tu mano y revela<br />
            lo que nadie más puede ver.
          </p>
        </motion.div>

        {/* ── SOCIAL PROOF BAR ───────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          style={{
            display:         'flex',
            alignItems:      'center',
            gap:             '1rem',
            background:      'rgba(255,215,0,0.05)',
            border:          '1px solid rgba(255,215,0,0.15)',
            borderRadius:    '50px',
            padding:         '0.55rem 1.6rem',
            marginBottom:    '2rem',
            flexWrap:        'wrap',
            justifyContent:  'center',
          }}
        >
          <span style={{ fontSize: '0.78rem', color: 'rgba(255,215,0,0.8)', fontFamily: 'var(--fuente-titulo)', letterSpacing: '0.05em' }}>
            ✨ <CounterAnimado /> lecturas
          </span>
          <span style={{ color: 'rgba(255,215,0,0.2)', fontSize: '0.7rem' }}>│</span>
          <Stars />
          <span style={{ fontSize: '0.75rem', color: 'var(--color-texto-suave)' }}>4.9 / 5</span>
        </motion.div>

        {/* ── CTA DORADO ─────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.6 }}
        >
          <button
            className="boton-cta"
            onClick={() => navigate('/lectura')}
          >
            🌙 Selene, léeme la mano →
          </button>
        </motion.div>

        {/* ── TRUST STRIP ────────────────────────────────── */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 0.6 }}
          style={{
            marginTop:     '1rem',
            color:         'rgba(138,138,170,0.6)',
            fontSize:      '0.76rem',
            letterSpacing: '0.06em',
          }}
        >
          🔒 Privado &nbsp;•&nbsp; ✨ Gratis &nbsp;•&nbsp; ⚡ Resultado en 30 seg
        </motion.p>

        {/* ── TESTIMONIO + AD ──────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.6 }}
          style={{
            display:        'flex',
            gap:            '0.6rem',
            flexWrap:       'wrap',
            justifyContent: 'center',
            alignItems:     'stretch',
            maxWidth:       '680px',
            marginTop:      '2.5rem',
          }}
        >
          {/* Solo el primer testimonio */}
          {[TESTIMONIOS[0]].map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.5 }}
              style={{
                flex:         '1 1 170px',
                maxWidth:     '200px',
                background:   t.color,
                border:       `1px solid ${t.borde}`,
                borderRadius: '12px',
                padding:      '0.75rem 0.9rem',
                textAlign:    'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem' }}>
                <div style={{
                  width:          '26px',
                  height:         '26px',
                  borderRadius:   '50%',
                  background:     'rgba(255,215,0,0.12)',
                  border:         '1px solid rgba(255,215,0,0.25)',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  fontFamily:     'var(--fuente-titulo)',
                  color:          'var(--color-oro)',
                  fontSize:       '0.72rem',
                  fontWeight:     '600',
                  flexShrink:     0,
                }}>
                  {t.inicial}
                </div>
                <div>
                  <div style={{ fontSize: '0.68rem', fontWeight: '600', color: 'var(--color-texto)', lineHeight: 1.2 }}>{t.nombre}</div>
                  <div style={{ fontSize: '0.58rem', color: 'var(--color-texto-suave)' }}>{t.lugar}</div>
                </div>
              </div>
              <Stars />
              <p style={{
                fontSize:        '0.66rem',
                color:           'var(--color-texto-suave)',
                lineHeight:      1.45,
                marginTop:       '0.35rem',
                fontStyle:       'italic',
                display:         '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow:        'hidden',
              }}>
                {t.texto}
              </p>
            </motion.div>
          ))}

          {/* AD-1: ocupa el espacio de los otros 2 testimonios */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.72, duration: 0.5 }}
            style={{ flex: '2 1 300px', maxWidth: '420px', display: 'flex', alignItems: 'center' }}
          >
            <AdUnit />
          </motion.div>
        </motion.div>


        {/* ── FOOTER ─────────────────────────────────────────────────── */}
        <div style={{ marginTop: '2rem', paddingBottom: '1.5rem',
          display: 'flex', gap: '1.2rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/privacidad"
            style={{ color: 'rgba(138,138,170,0.5)', fontSize: '0.72rem',
              textDecoration: 'none', letterSpacing: '0.04em' }}
          >
            Política de Privacidad
          </a>
          <span style={{ color: 'rgba(138,138,170,0.25)', fontSize: '0.72rem' }}>•</span>
          <span style={{ color: 'rgba(138,138,170,0.5)', fontSize: '0.72rem' }}>
            © 2026 Tu Mano Dice
          </span>
        </div>

      </div>
    </div>
  );
}

export default Landing;